<template>
<ClientOnly>
  <div class="flex flex-col gap-4 py-10">
    <VContainer>
      <h1 class="text-2xl font-bold mb-4">{{ $t('Checkout') }}</h1>
      <div class="bg-white" v-if="cart?.products?.length > 0">
        <div class="grid grid-cols-1 lg:grid-cols-[1fr_1fr] xl:grid-cols-[1fr_1fr] gap-8">
          <div class="flex flex-col gap-12 order-1 lg:order-0">
            <Step class="mt-8" :title="$t('Contact information')">
              <CheckoutAccount />
            </Step>

            <div v-if="authStore.isAuthenticated" class="flex flex-col gap-12">
              <Step :title="$t('Shipping information')">
                <CheckoutAddress type="id_address_delivery" :use-same-address-for-billing="changeBillingAddress" />
              </Step>

              <div v-if="cart?.id_address_delivery != '0'" class="flex flex-col gap-12">
                <Step :title="$t('Billing information')">
                  <label class="flex items-start gap-2 mb-4 pt-2">
                    <Checkbox type="checkbox" class="form-checkbox" v-model="changeBillingAddress" />
                    <span class="-mt-1">{{ $t('Same as shipping address') }}</span>
                  </label>
                  <CheckoutAddress v-if="!changeBillingAddress" :use-same-address-for-billing="false" type="id_address_invoice" />
                </Step>

                <Step :title="$t('Delivery method')">
                  <CheckoutDelivery v-if="authStore.isAuthenticated" />
                  <div v-if="isShippingReady" class="mt-6 pt-4 border-t border-gray-100">
                    <CheckoutMessage />
                  </div>
                </Step>

                <template v-if="isShippingReady">
                  <Step :title="$t('Payment method')">
                    <CheckoutPayment v-if="authStore.isAuthenticated" />
                  </Step>
                </template>
              </div>
            </div>
          </div>
          <div>
            <div v-if="cart" class="sticky top-10 flex flex-col gap-4 ml-auto w-full">
              <div>
                <ul role="list" class="divide-y divide-gray-200">
                  <VCartItem v-for="product in cart.products" :key="`${product.id_product}-${product.id_product_attribute}-${product.cart_quantity}`" :product="product" />
                </ul>
              </div>

              <div class="mt-4">
                <CheckoutVouchers v-if="authStore.isAuthenticated" />
              </div>

              <dl class="text-base flex flex-col gap-1">
                <div class="flex w-full">
                  <dt class="font-medium w-32">{{ $t('Subtotal') }}</dt>
                  <dd class="text-right w-full text-gray-800">
                    <span class="text-sm">({{ cart.subtotals?.tax.label }})</span> {{ cart.subtotals.products?.value }}
                  </dd>
                </div>
                <div class="flex w-full" v-if="cart.subtotals.discounts?.value">
                  <dt class="font-medium w-32">{{ $t('Discount') }}</dt>
                  <dd class="text-right w-full text-gray-800">-{{ cart.subtotals.discounts?.value }}</dd>
                </div>
                <div class="flex w-full text-gray-700 text-sm border-b">
                  <dt class="font-medium w-full">{{ $t('Taxes included') }}</dt>
                  <dd class="text-right w-full">{{ cart.subtotals?.tax?.value }}</dd>
                </div>
                <div class="flex w-full mt-3 border-b">
                  <dt class="font-medium w-32 whitespace-nowrap">
                    {{ $t('Shipping') }} -
                    <span v-if="currentCarrier" class="text-muted-foreground">{{ currentCarrier.name }}</span>
                  </dt>
                  <dd class="text-right w-full text-gray-800">{{ cart.subtotals.shipping?.value }}</dd>
                </div>
                <div class="flex w-full mt-3 border-b">
                  <div v-if="currentPudo" class="p-1">
                    <p class="font-medium text-gray-900">{{ currentPudo.name }}</p>
                    <p class="mt-1 text-sm text-gray-500">
                      <span class="block">{{ currentPudo.street }}</span>
                      <span class="block">{{ currentPudo.postCode }} {{ currentPudo.city }}</span>
                    </p>
                  </div>
                  <div v-else-if="isActive">
                    {{ $t('Select a pickup location') }}
                  </div>
                </div>
                <div class="flex w-full mt-3">
                  <dt class="font-medium w-32">{{ $t('Total') }}</dt>
                  <dd class="text-right w-full text-gray-800 text-lg font-bold">{{ cart.totals.total?.value }}</dd>
                </div>

              </dl>
            </div>
          </div>
        </div>
      </div>
      <div class="mx-auto max-w-2xl px-4 pb-24 pt-16 sm:px-6 lg:max-w-7xl lg:px-8" v-else>
        <VEmptyState
          :title="$t('No products in the cart')"
          :description="$t('Please add some products to the cart to continue')"
          :link="localePath('/')"
          :link-text="$t('Go to homepage')"
        />
      </div>
    </VContainer>
  </div>
</ClientOnly>
</template>

<script setup lang="ts">
import CheckoutAccount from './steps/CheckoutAccount.vue';
import CheckoutAddress from './steps/CheckoutAddress.vue';
import CheckoutDelivery from './steps/CheckoutDelivery.vue';
import CheckoutMessage from './steps/CheckoutMessage.vue';
import CheckoutPayment from './steps/CheckoutPayment.vue';
import CheckoutVouchers from './steps/CheckoutVouchers.vue';
import Step from './steps/Step.vue';

const { t: $t } = useI18n();
const localePath = useLocalePath();
const { data: cart } = useCart();
const { query: shippingQuery, currentCarrier, totalCarriers } = useCarriers();
const { mutationSelect } = useAddresses();
const { currentPudo, isActive } = usePudos();
const authStore = useAuth();
const changeBillingAddress = ref(true);

const isShippingReady = computed(
  () => !shippingQuery.isLoading && totalCarriers.value > 0 && (!isActive.value || currentPudo.value),
);

watch(changeBillingAddress, () => {
  if (!changeBillingAddress.value) {
    mutationSelect.mutate({ id_address_delivery: cart.value?.id_address_delivery });
  }
});
</script>
